import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Mail, ArrowUpRight, ArrowRight } from 'lucide-react';
import {
  ACCENT, FONT_DISPLAY, FONT_MONO, TEXT, HAIRLINE,
  glow, microLabel, accentOutlineButton,
} from '@/styles/premium';

interface GraduatedTimetableStateProps {
  userName?: string;
  /** Semester label from the academic calendar, e.g. "Spring 2026". */
  semesterLabel: string | null;
}

/**
 * Replaces the schedule on the personal-timetable screens once a student has
 * finished semester VIII. Renders inline (not full-screen) so the page's own
 * header, sidebar and bottom nav stay in place and the rest of the app is
 * still reachable.
 */
export default function GraduatedTimetableState({ userName, semesterLabel }: GraduatedTimetableStateProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          background: 'linear-gradient(135deg,rgba(20,184,166,.12) 0%,rgba(6,182,212,.07) 100%)',
          border: '1px solid rgba(20,184,166,.18)',
          borderRadius: 16,
          padding: 'clamp(22px,4.5vw,34px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ ...glow(220, 0.16), top: -70, right: -50 }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Label + icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'clamp(18px,3.5vw,24px)' }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                background: 'rgba(20,184,166,.15)',
                border: '1px solid rgba(20,184,166,.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <GraduationCap size={19} color={ACCENT} aria-hidden />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...microLabel, marginBottom: 3 }}>Degree complete</div>
              {semesterLabel && (
                <div style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.1em', color: TEXT.ghost }}>
                  SEMESTER VIII · {semesterLabel.toUpperCase()}
                </div>
              )}
            </div>
          </div>

          {/* Headline */}
          <h2
            style={{
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(26px,5vw,38px)',
              fontWeight: 400,
              letterSpacing: '-.03em',
              lineHeight: 1.05,
              marginBottom: 14,
            }}
          >
            {userName ? (
              <>Congratulations,{' '}
                <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.35)' }}>{userName}</em>
              </>
            ) : (
              <>Congratulations —{' '}
                <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.35)' }}>you're done</em>
              </>
            )}
          </h2>

          <p
            style={{
              fontSize: 'clamp(13px,1.6vw,14px)',
              lineHeight: 1.75,
              color: TEXT.muted,
              fontWeight: 300,
              marginBottom: 10,
              maxWidth: 460,
            }}
          >
            You have no classes left to show{semesterLabel ? <> — {semesterLabel} was your final semester</> : null}.
            Best of luck with whatever comes next.
          </p>
          <p
            style={{
              fontSize: 'clamp(12px,1.5vw,13px)',
              lineHeight: 1.7,
              color: TEXT.faint,
              fontWeight: 300,
              maxWidth: 460,
            }}
          >
            Everything else still works — exams, the academic calendar, bus routes and campus maps
            are all where you left them.
          </p>

          {/* Promo */}
          <div style={{ borderTop: `1px solid ${HAIRLINE}`, marginTop: 'clamp(20px,4vw,26px)', paddingTop: 'clamp(18px,3.5vw,24px)' }}>
            <div style={{ ...microLabel, marginBottom: 10 }}>Available for work</div>
            <p
              style={{
                fontSize: 'clamp(12px,1.5vw,13px)',
                lineHeight: 1.7,
                color: TEXT.faint,
                fontWeight: 300,
                marginBottom: 16,
                maxWidth: 460,
              }}
            >
              Need a developer for what's next? I build production ML systems, web apps and AI
              products — the same stack behind UniSchedule.
            </p>
            <div className="flex flex-col sm:flex-row" style={{ gap: 10, maxWidth: 460 }}>
              <PromoLink href="https://hakimi.tech" label="hakimi.tech" external />
              <PromoLink href="mailto:info@unischedule.me?subject=Project%20enquiry" label="info@unischedule.me" />
            </div>
          </div>
        </div>
      </div>

      {/* Where to go instead */}
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
        <NextLink label="Exam Timetable" to="/dashboard/exams" />
        <NextLink label="Academic Calendar" to="/dashboard/calendar" />
        <NextLink label="Bus Routes" to="/dashboard/bus" />
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PromoLink({ href, label, external }: { href: string; label: string; external?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...accentOutlineButton,
        flex: 1,
        minWidth: 0,
        background: hov ? 'rgba(20,184,166,.1)' : 'transparent',
        borderColor: hov ? 'rgba(20,184,166,.7)' : 'rgba(20,184,166,.5)',
      }}
    >
      {!external && <Mail size={13} aria-hidden />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {external && <ArrowUpRight size={13} aria-hidden />}
    </a>
  );
}

function NextLink({ label, to }: { label: string; to: string }) {
  const navigate = useNavigate();
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={() => navigate(to)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? 'rgba(255,255,255,.05)' : 'rgba(255,255,255,.03)',
        border: `1px solid ${hov ? 'rgba(255,255,255,.14)' : HAIRLINE}`,
        borderRadius: 12,
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        cursor: 'pointer', transition: '.2s', textAlign: 'left',
        fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: hov ? 'rgba(255,255,255,.75)' : TEXT.faint,
      }}
    >
      {label}
      <ArrowRight size={13} color={ACCENT} aria-hidden style={{ flexShrink: 0 }} />
    </button>
  );
}
